// How a question is NAMED to the person reading the screen.
//
// Three names exist for the same question and they are not interchangeable:
//
//   question_id   q_1785842391033   the kernel's record key. Stable, unique,
//                                   and meaningless to the author.
//   variable_name Qbeneficial       the column name in results and exports.
//   authored no.  Q2                what the author wrote in the Designer, and
//                                   the only one they use when they talk about
//                                   the survey ("what did Q2 get?").
//
// Every surface that names a question leads with the authored number, keeps the
// variable name beside it, and holds the record key back for support work. This
// module is the one place that decides which is which.
//
// UI-FREE: no React. Safe to unit-test in isolation.

/**
 * The author's own number for a question ("Q4", "Opinion"), or '' when they
 * never gave it one.
 *
 * The survey-structure endpoint calls it `display_id`; Analysis variables carry
 * it as `question_number`. Both are read, so either shape can be passed in.
 *
 * A node the author never numbered gets its record key echoed into display_id
 * by the descriptor writer. That is not a number anyone recognizes, so it reads
 * as absent — a raw id shown as "the author's number" would be a lie.
 */
export function authoredQuestionNumber(question) {
  const raw = question?.question_number ?? question?.number ?? question?.display_id;
  if (raw === undefined || raw === null) return '';
  const number = String(raw).trim();
  if (number === '') return '';
  const recordKey = String(question?.question_id ?? question?.id ?? '').trim();
  if (recordKey && number === recordKey) return '';
  return number;
}

/**
 * Question identity keyed by BOTH the record key and the variable name — the
 * same two keys the question-text lookup answers to, because a row reaching the
 * screen carries one or the other depending on where it came from.
 *
 * Each entry is { number, variable, id }: everything a cell needs to say which
 * question it is without going back to the structure.
 */
export function buildQuestionIdentity(questions = []) {
  const map = {};
  for (const question of questions) {
    if (!question) continue;
    const id = question.question_id ?? question.id;
    const identity = {
      number: authoredQuestionNumber(question),
      variable: question.variable_name || '',
      id: id == null ? '' : String(id),
    };
    if (identity.variable) map[identity.variable] = identity;
    if (identity.id) map[identity.id] = identity;
  }
  return map;
}

/**
 * The label for a question in a one-line cell: its authored number, falling back
 * to the variable name, falling back to the record key. Never blank when the row
 * names a question at all — a blank cell reads as "no question", which is a
 * different fact.
 */
export function questionLabel(key, identityByKey = {}) {
  const lookupKey = key == null ? '' : String(key);
  if (!lookupKey) return '';
  const identity = identityByKey[lookupKey];
  return identity?.number || identity?.variable || lookupKey;
}
